import {
  Accordion,
  ActionIcon,
  Box,
  ComboboxData,
  Grid,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styles from './styles.module.css';
import { useQuery } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { useCourseStore } from 'store/createCourseStore';
import { HiOutlineTrash } from 'react-icons/hi';
import SegmentedInput from 'components/SegmentedInput';
import { YES_NO_OPTION } from 'constant/course';
import { InputInformation } from 'modules/pages/lcms/courses/InputInformation';
import { showErrorNotification } from 'utils/notification';

interface ReviewApprovalFormProps {
  control: any;
  errors: any;
  employeeLecturerData: any;
  isLoadingEmployeeLecturer: boolean;
  setValue?: (_name: string, _value: any) => void;
}

const params = {
  pageSize: 10000,
  pageNumber: 1
};
const ReviewApprovalForm: React.FC<ReviewApprovalFormProps> = ({
  control,
  errors,
  setValue,
  employeeLecturerData,
  isLoadingEmployeeLecturer
}) => {
  const { mode, detailCourse } = useCourseStore();
  const [picData, setPICData] = useState<ComboboxData>([]);
  const [smeInternalData, setSmeInternalData] = useState<ComboboxData>([]);
  const [smeExternalData, setSmeExternalData] = useState<ComboboxData>([]);

  // Helper functions to reduce repetitive code
  const findSmeDetails = (smeId: string, smeType: 'smeInternal' | 'smeExternal') => {
    const smeList = detailCourse?.reviewApproval?.[smeType];
    if (!smeList) return null;

    const matchKey = smeType === 'smeExternal' ? 'email' : 'binusianID';
    const foundSme = smeList.find((item: any) => item[matchKey] === smeId);

    if (foundSme) {
      return `${foundSme.name || ''} - ${foundSme.email || foundSme.binusianID || ''}`;
    }
    return smeId;
  };

  const findPicDetails = (picId: string) => {
    const picList = detailCourse?.reviewApproval?.picApproval;
    if (!picList) return null;

    const foundPic = picList.find((item: any) => item.binusianID === picId);

    if (foundPic) {
      return `${foundPic.name || ''} - ${foundPic.email || ''}`;
    }
    return picId;
  };

  const { data: dataFacilitator } = useQuery({
    queryKey: ['getSMEFaciliator'],
    queryFn: () => courseService.getSMEFaciliator(params),
    staleTime: Infinity
  });

  useEffect(() => {
    if (employeeLecturerData && picData.length === 0) {
      const employees: any[] = [];
      const lecturers: any[] = [];

      if (employeeLecturerData?.employees?.data && employeeLecturerData.employees.data.length > 0) {
        employeeLecturerData.employees.data.forEach(
          (item: { binusianId: string; name: string }, index: number) => {
            employees.push({
              value: item.binusianId + '-employee',
              label: item.name || item.binusianId || 'Employee ' + (index + 1)
            });
          }
        );
      }

      if (employeeLecturerData?.lecturers?.data && employeeLecturerData.lecturers.data.length > 0) {
        employeeLecturerData.lecturers.data.forEach(
          (item: { binusianId: string; name: string }, index: number) => {
            lecturers.push({
              value: item.binusianId + '-lecturer',
              label: item.name || item.binusianId || 'Lecturer ' + (index + 1)
            });
          }
        );
      }
      const flattenedData = [...employees, ...lecturers];
      setPICData(flattenedData);
    }
  }, [employeeLecturerData, picData.length]);

  useEffect(() => {
    if (dataFacilitator?.data && dataFacilitator.data.length > 0) {
      const internalSME: any[] = [];
      const externalSME: any[] = [];

      dataFacilitator.data.forEach(
        (
          item: { binusianId: string; smeName: string; smeEmail: string; smeType: string },
          index: number
        ) => {
          const smeItem = {
            value: `${item.smeName || item.binusianId || item.smeEmail}-${index + 1}-${item.smeEmail}`,
            label: item.smeName || item.smeEmail || item.binusianId
          };

          if (item.smeType === 'I') {
            internalSME.push(smeItem);
          } else if (item.smeType === 'E') {
            externalSME.push({
              ...smeItem,
              value: `${item.smeName}-${index + 1}-${item.smeEmail}`
            });
          }
        }
      );

      setSmeInternalData(internalSME);
      setSmeExternalData(externalSME);
    }
  }, [dataFacilitator]);

  const handleRemovePICSME = (
    data: any,
    id: string,
    type: 'smeInternal' | 'smeExternal' | 'picApproval1' | 'picApproval2'
  ) => {
    if (type === 'smeInternal' || type === 'smeExternal') {
      const filteredData = data.filter((item: any) => item !== id);
      if (setValue) {
        setValue(type, filteredData);
      }
      return;
    }

    if (setValue) {
      setValue(type, '');
    }
  };

  // Reusable SME MultiSelect Component
  const SMEMultiSelectField = ({
    name,
    label,
    placeholder,
    data,
    smeType
  }: {
    name: string;
    label: string;
    placeholder: string;
    data: ComboboxData;
    smeType: 'smeInternal' | 'smeExternal';
  }) => {
    // Column configuration for SME DataTables
    const smeColumns: DataTableColumn[] = [
      {
        heading: 'Name',
        props: (row: any) => {
          const name = row.name.split('-')[0] || '-';
          const email = row.name.split('-')[row.name.split('-').length - 1] || '-';
          return (
            <Stack gap={0}>
              <Text size='sm' fw={600}>
                {name}
              </Text>
              <Text size='sm'>{email}</Text>
            </Stack>
          );
        },
        sortable: false,
        id: 'name',
        active: true
      },
      {
        heading: 'Action',
        props: (row: any) => (
          <ActionIcon
            variant='transparent'
            c='destructive'
            onClick={() => handleRemovePICSME(row.allValues, row.id, smeType)}
          >
            <HiOutlineTrash />
          </ActionIcon>
        ),
        sortable: false,
        id: 'action',
        active: true
      }
    ];

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          // Transform SME data for DataTables
          const smeTableData =
            field.value && field.value.length > 0
              ? field.value.map((sme: string) => ({
                  id: sme,
                  name: findSmeDetails(sme, smeType),
                  allValues: field.value
                }))
              : [];

          return (
            <>
              <MultiSelect
                label={label}
                placeholder={placeholder}
                data={data}
                error={errors[name]?.message}
                value={field.value}
                onChange={(values: string[]) => {
                  if (mode === 'edit') {
                    const currentValue = values[values.length - 1].split('-');
                    const shouldCheckValue = currentValue[currentValue.length - 1];
                    if (shouldCheckValue) {
                      const _defaultSME = field.value.map((item: string) => item.split('-')[1]);
                      const isExist = _defaultSME.includes(shouldCheckValue);
                      if (!isExist) {
                        field.onChange(values);
                        return;
                      }
                      showErrorNotification({
                        title: 'SME already selected',
                        message: 'Please select different SME'
                      });
                      return;
                    }
                    return;
                  }
                  field.onChange(values);
                }}
                disabled={isLoadingEmployeeLecturer}
                leftSection={<IconSearch />}
                searchable
                limit={20}
              />
              {mode === 'edit' && field.value && field.value.length > 0 && (
                <Paper mt={16} withBorder radius={0}>
                  <DataTables
                    columns={smeColumns}
                    data={smeTableData}
                    loading={false}
                    isNumbering={false}
                    withPagination={false}
                    emptyMessageTitle='No SME selected'
                    emptyMessageDescription='Select SME from the dropdown above'
                  />
                </Paper>
              )}
            </>
          );
        }}
      />
    );
  };

  // Reusable PIC Select Component
  const PICSelectField = ({
    name,
    label,
    placeholder
  }: {
    name: string;
    label: string;
    placeholder: string;
  }) => {
    // Column configuration for DataTables
    const picColumns: DataTableColumn[] = [
      {
        heading: 'Name',
        props: (row: any) => {
          const name = row.name.split('-')[0] || '-';
          const email = row.name.split('-')[row.name.split('-').length - 1] || '-';
          return (
            <Stack gap={0}>
              <Text size='sm' fw={600}>
                {name}
              </Text>
              <Text size='sm'>{email}</Text>
            </Stack>
          );
        },
        sortable: false,
        id: 'name',
        active: true
      },
      {
        heading: 'Action',
        props: (row: any) => (
          <ActionIcon
            variant='transparent'
            c='destructive'
            onClick={() =>
              handleRemovePICSME(row.id, row.id, name as 'picApproval1' | 'picApproval2')
            }
          >
            <HiOutlineTrash />
          </ActionIcon>
        ),
        sortable: false,
        id: 'action',
        active: true
      }
    ];

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          // Transform PIC data for DataTables
          const picTableData = field.value
            ? [
                {
                  id: field.value,
                  name: findPicDetails(field.value)
                }
              ]
            : [];

          return (
            <>
              <Select
                label={label}
                placeholder={placeholder}
                data={picData}
                error={errors[name]?.message}
                value={field.value}
                onChange={(value) => field.onChange(value || '')}
                searchable
                leftSection={<IconSearch />}
                disabled={isLoadingEmployeeLecturer}
                limit={20}
              />
              {mode === 'edit' && field.value && (
                <Paper mt={16} withBorder radius={0}>
                  <DataTables
                    columns={picColumns}
                    data={picTableData}
                    loading={false}
                    isNumbering={false}
                    withPagination={false}
                    emptyMessageTitle='No PIC selected'
                    emptyMessageDescription='Select a PIC from the dropdown above'
                  />
                </Paper>
              )}
            </>
          );
        }}
      />
    );
  };

  return (
    <Accordion.Item className={styles.accordionItem} value='reviewApproval'>
      <Accordion.Control className={styles.accordionControl}>Review & Approval</Accordion.Control>
      <Accordion.Panel className={styles.accordionPanel}>
        <Stack gap='lg' py={16}>
          <Card>
            <Controller
              name='flagReviewSME'
              control={control}
              render={({ field }) => (
                <Group align='flex-start' gap={0}>
                  <SegmentedInput
                    label='This course needs a review by SMEs'
                    options={YES_NO_OPTION}
                    value={
                      YES_NO_OPTION.find((item) => (field.value ? 'Yes' : 'No') === item.label)
                        ?.value || '1'
                    }
                    onChange={(value: string) =>
                      field.onChange(
                        YES_NO_OPTION.find((item) => item.value === value)?.label === 'Yes'
                          ? true
                          : false
                      )
                    }
                  />
                  <Box mt={-4}>
                    <InputInformation description='The assigned SMEs will review this course. Selecting "Yes" will trigger an email containing the link page for input materials.' />
                  </Box>
                </Group>
              )}
            />

            <Grid>
              <Grid.Col span={6}>
                <SMEMultiSelectField
                  name='smeInternal'
                  label='Internal SME'
                  placeholder='Select internal SMEs'
                  data={smeInternalData}
                  smeType='smeInternal'
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <SMEMultiSelectField
                  name='smeExternal'
                  label='External SME'
                  placeholder='Select external SMEs'
                  data={smeExternalData}
                  smeType='smeExternal'
                />
              </Grid.Col>
            </Grid>
          </Card>

          <Card>
            <Text size='sm' fw={500} mb={16}>
              PIC Approval
            </Text>

            <Grid>
              <Grid.Col span={6}>
                <PICSelectField
                  name='picApproval1'
                  label='PIC Approval 1'
                  placeholder='Select PIC Approval 1'
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <PICSelectField
                  name='picApproval2'
                  label='PIC Approval 2'
                  placeholder='Select PIC Approval 2'
                />
              </Grid.Col>
            </Grid>
          </Card>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default ReviewApprovalForm;
